import { useState, useMemo, useEffect, useCallback } from 'react';
import { X, FileSpreadsheet } from 'lucide-react';
import { useConfig } from '../context/ConfigContext';
import { usePosSession } from '../context/PosSessionContext';
import { spinnerRing } from '../utils/bookingUtils';
import { formatMoney } from '../utils/currencyUtil';
import { buildWorkbookBlob, downloadWorkbook } from '../utils/xlsxUtil';
import {
  fetchPosOrdersInRange, summarisePosSales, rankBestSellers, bucketPosSalesOverTime,
  listPosCashiers, filterPosOrdersByCashier, posCashierKey,
  subscribePosPaymentMethods, subscribePosProducts, posCostLookup, posOrderCost,
  posOrderIsDeleted, filterPosSaleRecords, posDateTimeText,
} from '../utils/posUtil';
import {
  POS_PAGE_SIZE,
  POS_SALES_GRANULARITIES,
  POS_DEFAULT_SALES_GRANULARITY,
  POS_DEFAULT_RANGE_DAYS,
  POS_TREND_MEASURES,
  POS_DEFAULT_TREND_MEASURE,
  POS_TREND_VIEWS,
  POS_DEFAULT_TREND_VIEW,
  POS_CATEGORY_COLORS,
  POS_TREND_MAX_SERIES,
  POS_TREND_OTHER_LABEL,
  POS_SALES_SCOPE_DEFAULT,
  POS_PAYMENT_FILTER_ALL,
  POS_DEFAULT_PAYMENT_METHOD,
  POS_ORDER_DELETION_KEY,
  POS_SALE_STATUS_DEFAULT,
  POS_SALE_STATUS_COMPLETED,
  POS_SALE_STATUS_DELETED,
  POS_HOURLY_GRANULARITY,
  POS_HOURLY_GRANULARITY_LABEL,
} from '../constants/posConst';
import { posOrderItemLabel } from '../utils/posCustomizationUtil';
import PosSelect from './PosSelect';
import PosReceipt from './PosReceipt';
import PosPager from './PosPager';
import PosStaffFilter from './PosStaffFilter';
import PosSalesScopeToggle from './PosSalesScopeToggle';
import PosSalesTrendChart from './PosSalesTrendChart';
import PosCategoryBars from './PosCategoryBars';
import PosDeleteSaleModal from './PosDeleteSaleModal';
import PosSaleDeletionNote from './PosSaleDeletionNote';
import PosSaleRecordFilters from './PosSaleRecordFilters';
import Modal from './Modal';
import '../css/Pos.css';

/**
 * A date as the YYYY-MM-DD an <input type="date"> expects.
 *
 * 'en-CA' is the shortest way to get that shape out of a local date without
 * hand-padding the parts. Deliberately the DEVICE's local day, because
 * fetchPosOrdersInRange turns these strings into local-midnight timestamps —
 * a day taken from one zone and bounded in another would drop the evening's
 * orders.
 *
 * @param {Date} date
 * @returns {string} e.g. '2026-09-09'
 */
const localDateStr = (date) => date.toLocaleDateString('en-CA');

/**
 * The period the screen opens on: the last POS_DEFAULT_RANGE_DAYS days,
 * counting today as one of them — today alone by default.
 *
 * Both ends are computed from the same call so a run that straddles midnight
 * cannot return a range whose start and end came from different days.
 *
 * @returns {{from: string, to: string}} inclusive bounds, YYYY-MM-DD
 */
const defaultRange = () => {
  const end = new Date();
  const start = new Date(end);
  // Minus one, because today is already the first of the N days.
  start.setDate(start.getDate() - (POS_DEFAULT_RANGE_DAYS - 1));
  return { from: localDateStr(start), to: localDateStr(end) };
};

/**
 * A loaded range as a heading, collapsed to one date when both ends match.
 *
 * Numeric rather than 'Sep 15, 2026': this label appears three times on the
 * screen — the summary title, the subtitle and the empty state — and the short
 * form keeps the title on one line at the width the panel actually has.
 *
 * @param {string} from - inclusive start, YYYY-MM-DD
 * @param {string} to - inclusive end, YYYY-MM-DD
 * @returns {string} e.g. '9/21/2026' or '9/15/2026 to 9/21/2026'
 */
const rangeLabel = (from, to) => {
  const pretty = (value) => {
    const [y, m, d] = String(value || '').split('-').map(Number);
    if (!y || !m || !d) return value || '';
    return new Date(y, m - 1, d).toLocaleDateString('en-US');
  };
  return from === to ? pretty(from) : `${pretty(from)} to ${pretty(to)}`;
};

/**
 * Sales — a report over a date range, laid out like the booking export
 * screen: read the breakdown, page through the records, export to Excel.
 *
 * Opens on TODAY'S takings rather than an empty screen: "how are we doing
 * today" is the question this screen is opened for, so it is answered on
 * arrival and picking a range is reserved for looking further back. A
 * single-day report is charted by the hour, so it still has a shape.
 *
 * @returns {JSX.Element}
 */
function PosSales() {
  const { config, clientId } = useConfig();
  // Reading the takings and carrying a copy of them out of the building are
  // separate permissions, so the export is gated on its own rather than
  // riding on the fact that this screen opened at all.
  const { can, isStoreAdmin, staffEmail } = usePosSession();
  const canExport = can('exportSales');
  // Deleting a sale is a status change only (see deletePosOrder), but it takes
  // money out of every report, so it is a permission of its own.
  const canDelete = can('deleteSales');
  // WHOSE SALES. A store admin filters by any member of staff; everyone else
  // chooses between their own takings and the club's overall total — never
  // another named person's.
  const [salesScope, setSalesScope] = useState(POS_SALES_SCOPE_DEFAULT);
  // Defined per component rather than at module scope: the symbol comes from
  // Config -> System, so it needs `config` in hand. Call sites read the same
  // as they always did.
  const peso = (value) => formatMoney(value, config);
  const [startDate, setStartDate] = useState(() => defaultRange().from);
  const [endDate, setEndDate] = useState(() => defaultRange().to);
  // WHAT WAS FETCHED, before the staff filter. `orders` below is the filtered
  // view, and everything on the screen reads that — so the summary, the chart,
  // the breakdown, the preview and the export all agree about which orders the
  // report covers.
  const [allOrders, setAllOrders] = useState(null);
  // Cashier keys the user has UN-TICKED. Empty means everyone, which is both
  // the default and what a freshly-fetched range should mean.
  const [excludedStaff, setExcludedStaff] = useState(() => new Set());
  // Mode of payment filter: POS_PAYMENT_FILTER_ALL or one method key. The
  // menu is this club's own configured methods (Config -> Payments).
  const [paymentFilter, setPaymentFilter] = useState(POS_PAYMENT_FILTER_ALL);
  const [payMethods, setPayMethods] = useState([]);

  useEffect(() => subscribePosPaymentMethods(clientId, setPayMethods), [clientId]);

  // SHOW COST PRICE (PUHUNAN) — on by default. Adds what the goods cost
  // beside what they sold for: in the breakdown, the preview and the chart.
  const [showCost, setShowCost] = useState(true);
  // product id -> the product's CURRENT cost price, the fallback for sales
  // recorded before costs were copied onto each line. Read only while costs
  // are shown, so a screen with them hidden does not download the catalogue.
  const [costLookup, setCostLookup] = useState(() => new Map());
  useEffect(() => {
    if (!showCost) return undefined;
    return subscribePosProducts(clientId, (list) => setCostLookup(posCostLookup(list)));
  }, [clientId, showCost]);

  /**
   * A cost figure, or a dash when none is known — never ₱0, which would read
   * as "this cost nothing".
   *
   * @param {number|null} value
   * @returns {string}
   */
  const costText = (value) => (value === null || value === undefined ? '—' : peso(value));

  const paymentOptions = useMemo(() => [
    { key: POS_PAYMENT_FILTER_ALL, label: 'All' },
    ...payMethods.map(({ key, label }) => ({ key, label })),
  ], [payMethods]);

  // A method removed from Config while it was the filter would leave a
  // selection the menu no longer offers; fall back to All.
  useEffect(() => {
    if (paymentFilter === POS_PAYMENT_FILTER_ALL || payMethods.length === 0) return;
    if (!payMethods.some((m) => m.key === paymentFilter)) setPaymentFilter(POS_PAYMENT_FILTER_ALL);
  }, [payMethods, paymentFilter]);
  const [fetching, setFetching] = useState(false);
  const [page, setPage] = useState(1);
  const [viewing, setViewing] = useState(null);
  // The delete-sale prompt, open over the receipt being viewed.
  const [deletingSale, setDeletingSale] = useState(false);
  // The preview table's own filters. They narrow ONLY the table — the summary,
  // chart, breakdown and export always cover every completed sale in range.
  const [statusFilter, setStatusFilter] = useState(POS_SALE_STATUS_DEFAULT);
  const [orderIdSearch, setOrderIdSearch] = useState('');
  const [recordSearch, setRecordSearch] = useState('');
  const [modal, setModal] = useState(null);
  // The range the rows on screen actually came from, which is not the same as
  // the two inputs: those change as soon as staff touch them, and labelling
  // the table with a range it was never fetched for would misreport the
  // figures under it.
  const [loadedRange, setLoadedRange] = useState(null);

  // How the trend line buckets the period. Kept out of `filters` because it
  // re-shapes what is already loaded rather than asking Firebase for anything
  // — switching Daily to Monthly must not cost a refetch.
  const [granularity, setGranularity] = useState(POS_DEFAULT_SALES_GRANULARITY);
  // What the y axis counts, and whether it is one line or one per category.
  // Both only re-read data that is already loaded, so neither costs a fetch.
  const [measure, setMeasure] = useState(POS_DEFAULT_TREND_MEASURE);
  const [view, setView] = useState(POS_DEFAULT_TREND_VIEW);

  // Who took money in the loaded range, and how much each of them took.
  // Rebuilt from whatever was fetched, so it describes the period on screen
  // rather than the club's staff list as it stands today.
  // Completed sales only, so each person's takings exclude deleted sales.
  const staffOptions = useMemo(
    () => listPosCashiers((allOrders || []).filter((order) => !posOrderIsDeleted(order))),
    [allOrders],
  );

  // Every fetched sale — completed AND deleted — after the payment and staff
  // filters. The preview table lists from this; the reports read `orders`.
  const scopedOrders = useMemo(() => {
    if (!allOrders) return null;
    // Applied BEFORE the staff filter and to everything below it — summary,
    // chart, breakdown, preview and export — so they all describe the same
    // orders. An order from before payment methods existed carries none, and
    // those were cash sales.
    const byPayment = paymentFilter === POS_PAYMENT_FILTER_ALL
      ? allOrders
      : allOrders.filter((order) => (order?.paymentMethod || POS_DEFAULT_PAYMENT_METHOD) === paymentFilter);
    if (isStoreAdmin) return filterPosOrdersByCashier(byPayment, excludedStaff);
    if (salesScope === 'mine') {
      const me = posCashierKey(staffEmail);
      return byPayment.filter((order) => posCashierKey(order?.cashier) === me);
    }
    return [...byPayment];
  }, [allOrders, excludedStaff, isStoreAdmin, salesScope, staffEmail, paymentFilter]);

  // COMPLETED sales: what every figure, chart and export sheet sums. A
  // deleted sale took no money, so it never reaches any of them.
  const orders = useMemo(
    () => (scopedOrders ? scopedOrders.filter((order) => !posOrderIsDeleted(order)) : null),
    [scopedOrders],
  );
  const deletedOrders = useMemo(
    () => (scopedOrders ? scopedOrders.filter(posOrderIsDeleted) : []),
    [scopedOrders],
  );

  // What the preview table lists, after its status and search filters.
  const previewOrders = useMemo(
    () => filterPosSaleRecords(scopedOrders, { status: statusFilter, orderId: orderIdSearch, search: recordSearch }),
    [scopedOrders, statusFilter, orderIdSearch, recordSearch],
  );
  // The reason column appears whenever deleted sales can be in the list.
  const showDeletion = statusFilter !== POS_SALE_STATUS_COMPLETED;
  // The table's TOTAL row. On "All" it sums only the completed rows: adding a
  // deleted sale's amount to real takings would overstate them.
  const previewTotals = useMemo(() => summarisePosSales(
    statusFilter === POS_SALE_STATUS_DELETED
      ? previewOrders
      : previewOrders.filter((order) => !posOrderIsDeleted(order)),
    { costLookup: showCost ? costLookup : undefined },
  ), [previewOrders, statusFilter, showCost, costLookup]);

  // Back to the first page whenever the filter changes what is in the list.
  useEffect(() => { setPage(1); }, [excludedStaff, salesScope, paymentFilter, statusFilter, orderIdSearch, recordSearch]);

  // The Mode of Payment dropdown. Rendered beside the chart controls, and in
  // the empty state too, so a filter that emptied the report can be undone.
  const paymentControl = (
    <PosSelect
      className="pos-payfilter"
      label="Payment"
      options={paymentOptions}
      value={paymentFilter}
      onChange={setPaymentFilter}
    />
  );

  // The whose-sales control: the per-person filter for a store admin, the
  // My sales / Overall choice for everyone else.
  const scopeControl = isStoreAdmin
    ? <PosStaffFilter staff={staffOptions} excluded={excludedStaff} onChange={setExcludedStaff} />
    : <PosSalesScopeToggle value={salesScope} onChange={setSalesScope} />;

  const summary = useMemo(
    () => (orders ? summarisePosSales(orders, { costLookup: showCost ? costLookup : undefined }) : null),
    [orders, showCost, costLookup],
  );

  // A ONE-DAY report is always charted by the hour: Daily, Weekly, Monthly or
  // Yearly would all collapse it to a single point. Read off the LOADED range,
  // like the chart itself, and the staff's own choice is kept for when they
  // fetch a longer range again.
  const singleDay = Boolean(loadedRange) && loadedRange.from === loadedRange.to;
  const chartGranularity = singleDay ? POS_HOURLY_GRANULARITY : granularity;

  // Bucketed against the LOADED range, not the inputs, so the line's x axis
  // covers exactly the period the figures above it describe.
  const trend = useMemo(
    () => (orders && loadedRange
      ? bucketPosSalesOverTime(orders, chartGranularity, loadedRange, { costLookup: showCost ? costLookup : undefined })
      : { points: [], categories: [], truncated: false }),
    [orders, chartGranularity, loadedRange, showCost, costLookup],
  );

  const granularityLabel = singleDay
    ? POS_HOURLY_GRANULARITY_LABEL
    : (POS_SALES_GRANULARITIES.find(g => g.key === granularity)?.label || '');

  const byQuantity = measure === 'quantity';

  /**
   * One plotted value, in the units the Amount/Quantity toggle is showing.
   *
   * @param {number} value
   * @returns {string}
   */
  const formatMeasure = (value) => (byQuantity
    ? `${Math.round(Number(value) || 0).toLocaleString()}`
    : peso(value));

  // The lines themselves. Overall is one series off the point totals;
  // Category is one per category, with everything past the colour list summed
  // into "Other" rather than a ninth line repeating slot one's colour.
  const series = useMemo(() => {
    const { points, categories } = trend;
    if (!points.length) return [];

    if (view === 'overall') {
      return [{
        key: 'overall',
        label: 'All categories',
        color: POS_CATEGORY_COLORS[0],
        values: points.map(p => (byQuantity ? p.quantity : p.amount)),
      }];
    }

    const named = categories.slice(0, POS_TREND_MAX_SERIES);
    const rest = categories.slice(POS_TREND_MAX_SERIES);
    const valueIn = (point, name) => {
      const bucket = point.byCategory[name];
      if (!bucket) return 0;
      return byQuantity ? bucket.quantity : bucket.amount;
    };

    const lines = named.map((name, i) => ({
      key: name,
      label: name,
      color: POS_CATEGORY_COLORS[i],
      values: points.map(p => valueIn(p, name)),
    }));

    if (rest.length) {
      lines.push({
        key: '__other__',
        label: `${POS_TREND_OTHER_LABEL} (${rest.length})`,
        // The last slot, shared by the bucket — never a generated hue, which
        // would read as a category of its own.
        color: POS_CATEGORY_COLORS[POS_CATEGORY_COLORS.length - 1],
        values: points.map(p => rest.reduce((sum, name) => sum + valueIn(p, name), 0)),
      });
    }
    return lines;
  }, [trend, view, byQuantity]);

  // One hover line per period. On Overall it carries the per-category split
  // the toggle would otherwise hide — which is the whole point of hovering an
  // aggregate — plus VAT, so a tax-exclusive breakdown still adds up to the
  // plotted figure.
  const tooltips = useMemo(() => trend.points.map((point, i) => {
    const head = `${point.label}: ${formatMeasure(byQuantity ? point.quantity : point.amount)}`
      + ` · ${point.orderCount} order${point.orderCount === 1 ? '' : 's'}`
      // The period's cost, when costs are shown and the chart is in money.
      + (showCost && !byQuantity && point.cost !== null ? ` · Cost ${peso(point.cost)}` : '');
    if (view !== 'overall') {
      return `${head}\n${series.map(s => `${s.label}: ${formatMeasure(s.values[i])}`).join('\n')}`;
    }
    const split = Object.entries(point.byCategory)
      .map(([name, b]) => [name, byQuantity ? b.quantity : b.amount])
      .filter(([, v]) => v > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([name, v]) => `${name}: ${formatMeasure(v)}`);
    if (!byQuantity && point.vat > 0) split.push(`VAT: ${peso(point.vat)}`);
    return split.length ? `${head}\n${split.join('\n')}` : head;
  }), [trend.points, view, byQuantity, series, showCost]);

  // The breakdown re-ranked by whatever the chart is measuring, so the table
  // and the lines above it always agree about what "top" means. On quantity a
  // category of cheap fast movers outranks one expensive bottle, which is the
  // opposite of the revenue order — presenting one while plotting the other
  // is how a reader concludes the chart is wrong.
  const rankedGroups = useMemo(() => {
    if (!summary) return [];
    const of = (row) => (byQuantity ? row.quantity : row.revenue);
    return summary.groups
      .map(group => ({
        ...group,
        quantity: group.products.reduce((sum, p) => sum + (Number(p.quantity) || 0), 0),
        products: [...group.products].sort((a, b) => of(b) - of(a)),
      }))
      .sort((a, b) => of(b) - of(a));
  }, [summary, byQuantity]);

  const pageRows = previewOrders.slice((page - 1) * POS_PAGE_SIZE, page * POS_PAGE_SIZE);

  /**
   * A sale was just deleted from its receipt: mark it in the loaded list (no
   * refetch — the change is a single known field), close both dialogs and
   * confirm.
   *
   * @param {{deletion: object}} result - from deletePosOrder
   * @returns {void}
   */
  const handleSaleDeleted = ({ deletion }) => {
    const id = viewing?.id;
    setAllOrders((list) => (list || []).map((order) => (
      order.id === id ? { ...order, [POS_ORDER_DELETION_KEY]: deletion } : order
    )));
    setDeletingSale(false);
    setViewing(null);
    setModal({
      title: 'Sale deleted',
      message: 'The sale\'s status is now Cancelled/Deleted. It is kept on record with your reason and no longer counts in the totals. Choose Cancelled/Deleted in the preview to see it.',
      type: 'success',
    });
  };

  /**
   * Loads the orders for one date range into the report.
   *
   * Takes its dates as arguments rather than reading state so the same
   * function serves both callers: the mount effect below, which must run
   * against today whatever the inputs say, and the Fetch Sales button.
   *
   * @param {string} from - inclusive start, YYYY-MM-DD
   * @param {string} to - inclusive end, YYYY-MM-DD
   * @returns {Promise<void>}
   */
  const loadSales = useCallback(async (from, to) => {
    setFetching(true);
    try {
      const list = await fetchPosOrdersInRange(clientId, from, to);
      setAllOrders(list);
      setLoadedRange({ from, to });
      setPage(1);
      // The staff filter is NOT cleared on a refetch. Someone comparing two
      // weeks for one member of staff would have to re-tick the other five
      // every time, and anyone who first appears in the new range arrives
      // included anyway — that is what tracking the un-ticked set buys.
    } catch (error) {
      setModal({ title: 'Could not load sales', message: error.message, type: 'error' });
    } finally {
      setFetching(false);
    }
  }, []);

  // The last week's sales, once, on arrival. Pinned to defaultRange() rather
  // than to the startDate/endDate state so this cannot re-run and overwrite a
  // range staff have since fetched.
  useEffect(() => {
    const { from, to } = defaultRange();
    loadSales(from, to);
  }, [loadSales]);

  const handleFetch = () => loadSales(startDate, endDate);

  /**
   * Three sheets: the orders, every line item, and the best sellers.
   *
   * Line items get their own sheet rather than being crammed into the order
   * rows: one order holds several products, and a spreadsheet can only pivot
   * or sum what sits one-per-row.
   */
  const handleExport = () => {
    if (!scopedOrders?.length) return;

    const money = (value) => ({ value: Number(value) || 0, type: 'money' });
    const count = (value) => ({ value: Number(value) || 0, type: 'number' });
    const text = (value) => ({ value: value ?? '' });

    // Subtotal and VAT get columns of their own rather than being folded into
    // Total: this is the sheet an accountant reconciles against, and a total
    // with tax silently inside it is the figure they would have to unpick by
    // hand. Both are blank on an order taken with no VAT, which is honest —
    // there was no tax to report, rather than a zero that was charged.
    const orderSheet = {
      name: 'Orders',
      columnWidths: [26, 20, 10, 14, 12, 14, 14, 14, 14, 18, 28],
      rows: [
        { variant: 'header', cells: ['Order ID', 'Date', 'Items', 'Subtotal', 'VAT', 'Delivery fee', 'Total', 'Cash', 'Change', 'Payment', 'Cashier'].map(text) },
        ...orders.map(order => ({
          cells: [
            text(order.id),
            text(new Date(order.createdAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })),
            count(order.itemCount),
            // Falls back to the total for an order recorded before VAT
            // existed: there was no tax, so the two were the same figure.
            money(order.subtotal ?? order.total),
            // BLANK, not zero, when no tax was charged — a 0 in a VAT column
            // reads as "we charged nothing on a taxable sale".
            order.vat ? money(order.vat) : text(''),
            order.deliveryFee ? money(order.deliveryFee) : text(''),
            money(order.total),
            money(order.cashReceived),
            money(order.change),
            text(order.paymentLabel || order.paymentMethod || ''),
            text(order.cashier),
          ],
        })),
        {
          variant: 'footer',
          cells: [
            text('TOTAL'), text(''),
            count(orders.reduce((n, o) => n + (Number(o.itemCount) || 0), 0)),
            // Each footer figure sums the column directly above it: the line
            // totals, the tax, then what was actually taken.
            money(summary?.total),
            summary?.vat ? money(summary.vat) : text(''),
            summary?.deliveryFees ? money(summary.deliveryFees) : text(''),
            money(summary?.grossTotal),
            text(''), text(''), text(''), text(''),
          ],
        },
      ],
    };

    const itemRows = orders.flatMap(order => (order.items || []).map(item => ({
      cells: [
        text(order.id),
        text(new Date(order.createdAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })),
        // With its options, so a 50% and a 100% sugar line can be told apart.
        text(posOrderItemLabel(item)),
        text(item.category || 'Uncategorised'),
        count(item.quantity),
        money(item.price),
        money(item.lineTotal),
      ],
    })));

    const itemSheet = {
      name: 'Items',
      columnWidths: [26, 20, 28, 18, 8, 12, 14],
      rows: [
        { variant: 'header', cells: ['Order ID', 'Date', 'Product', 'Category', 'Qty', 'Unit price', 'Line total'].map(text) },
        ...itemRows,
        {
          variant: 'footer',
          cells: [
            text('TOTAL'), text(''), text(''), text(''),
            count(summary?.itemCount),
            text(''),
            // NET: this column lists line totals, which carry no tax.
            money(summary?.total),
          ],
        },
      ],
    };

    const bestSheet = {
      name: 'Best sellers',
      columnWidths: [28, 10, 14, 10],
      rows: [
        { variant: 'header', cells: ['Product', 'Units', 'Revenue', 'Share'].map(text) },
        ...rankBestSellers(orders, 100).map(row => ({
          cells: [
            text(row.name),
            count(row.quantity),
            money(row.revenue),
            // Against the NET total: row.revenue is a line-total sum, so
            // dividing by the tax-added figure would make the shares sum to
            // less than 100%.
            text(summary?.total ? `${((row.revenue / summary.total) * 100).toFixed(1)}%` : '—'),
          ],
        })),
      ],
    };

    // Deleted sales on a sheet of their own, with why — never mixed into the
    // Orders sheet, whose totals are the takings. Added only when there are any.
    const deletedSheet = {
      name: 'Deleted sales',
      columnWidths: [26, 20, 40, 14, 18, 28, 20, 28, 44],
      rows: [
        { variant: 'header', cells: ['Order ID', 'Date', 'Items', 'Total', 'Payment', 'Cashier', 'Deleted on', 'Deleted by', 'Reason'].map(text) },
        ...deletedOrders.map((order) => {
          const deletion = order[POS_ORDER_DELETION_KEY] || {};
          return {
            cells: [
              text(order.id),
              text(posDateTimeText(order.createdAt)),
              text((order.items || []).map(i => `${posOrderItemLabel(i)} (${i.quantity})`).join(', ')),
              money(order.total),
              text(order.paymentLabel || order.paymentMethod || ''),
              text(order.cashier),
              text(posDateTimeText(deletion.deletedAt || deletion.deletedAtMs)),
              text(deletion.deletedBy),
              text(deletion.reason),
            ],
          };
        }),
        {
          variant: 'footer',
          cells: [
            text('TOTAL'), text(''), text(''),
            money(deletedOrders.reduce((sum, o) => sum + (Number(o.total) || 0), 0)),
            text(''), text(''), text(''), text(''), text(''),
          ],
        },
      ],
    };

    const sheets = [orderSheet, itemSheet, bestSheet];
    if (deletedOrders.length) sheets.push(deletedSheet);
    const blob = buildWorkbookBlob({ sheets });
    // Named for the range the rows CAME from, not the inputs — staff often
    // retype the dates before exporting, and a file called by the range they
    // are about to fetch would be filed under the wrong period.
    downloadWorkbook(blob, `pos-sales-${loadedRange?.from}-to-${loadedRange?.to}.xlsx`);
  };

  return (
    <div className="pos-sales">
      {/* ---------- Range ---------- */}
      <section className="pos-section">
        <div className="pos-section-head">
          <h2>Sales Reports</h2>
          {/* Names the range the figures below actually cover, so "today" on
              first load and the chosen period afterwards read the same way —
              a fixed "showing today's sales" line would go quietly wrong the
              moment staff fetched last week. */}
          <p>
            {loadedRange
              ? <>Showing sales for <strong>{rangeLabel(loadedRange.from, loadedRange.to)}</strong>. Select a date range to report on another period.</>
              : 'Generate a sales report by selecting a date range.'}
          </p>
        </div>

        <div className="pos-range">
          <label className="pos-field">
            <span>Start Date</span>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </label>
          <label className="pos-field">
            <span>End Date</span>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </label>
          <button type="button" className="pos-btn pos-btn-primary pos-fetch" onClick={handleFetch} disabled={fetching}>
            {fetching ? 'Fetching…' : 'Fetch Sales'}
          </button>
        </div>

        {/* Only changes what is SHOWN — the figures are already loaded, so
            ticking it never costs a refetch. */}
        <label className="pos-check pos-cost-toggle">
          <input type="checkbox" checked={showCost} onChange={(e) => setShowCost(e.target.checked)} />
          <span>
            <strong>Show cost price (puhunan)</strong>
            <em>
              Sales recorded before a product had a cost price use its current cost price.
              A dash means the product has no cost price yet.
            </em>
          </span>
        </label>
      </section>

      {fetching && <div className="pos-loading">{spinnerRing('Loading sales…')}</div>}

      {/* Nothing was taken in the period at all. */}
      {!fetching && allOrders && allOrders.length === 0 && (
        <section className="pos-section">
          <p className="pos-empty-line">
            No orders for {loadedRange ? rangeLabel(loadedRange.from, loadedRange.to) : 'this range'}.
          </p>
        </section>
      )}

      {/* There were orders, but the staff filter is hiding all of them. Said
          differently from the line above, and with the way out attached —
          "no orders" here would be untrue and would send someone off to check
          the dates. */}
      {!fetching && allOrders && allOrders.length > 0 && scopedOrders.length === 0 && (
        <section className="pos-section">
          {scopeControl}
          <div className="pos-payfilter-row">{paymentControl}</div>
          <p className="pos-empty-line">
            {paymentFilter !== POS_PAYMENT_FILTER_ALL
              ? 'No orders match this mode of payment. Choose All to see every order.'
              : (isStoreAdmin
                ? 'No staff selected, so there is nothing to report. Tick someone above, or use Select all.'
                : 'You have no sales in this range. Choose Overall to see the whole store.')}
          </p>
        </section>
      )}

      {/* There are sales in scope, but every one of them was deleted — so
          there are no takings to chart. The filters stay reachable here, and
          the deleted sales are still listed in the preview below. */}
      {!fetching && scopedOrders && scopedOrders.length > 0 && orders.length === 0 && (
        <section className="pos-section">
          {scopeControl}
          <div className="pos-payfilter-row">{paymentControl}</div>
          <p className="pos-empty-line">
            Every sale in this range has been deleted, so there are no takings to report.
            Choose Cancelled/Deleted in the preview below to see them.
          </p>
        </section>
      )}

      {!fetching && orders && orders.length > 0 && (
        <>
          {/* ---------- Chart + breakdown ---------- */}
          <section className="pos-section">
            {/* No legend: one series, and every bar is already named on its
                own row — the legend was restating the chart. */}
            {/* Names the period the figures below cover. Without it the big
                total is an unlabelled number — fine while you are the one who
                just picked the dates, not when the screen is read back later
                or handed to someone else. Driven by loadedRange, not the two
                inputs, so retyping the dates without fetching cannot make the
                title lie about what is on screen. */}
            {loadedRange && (
              <h2 className="pos-summary-title">
                Sales Summary ({rangeLabel(loadedRange.from, loadedRange.to)})
              </h2>
            )}

            {/* Between the title and the figures, because it CHANGES those
                figures — a filter placed under them would be read after the
                total it explains. */}
            {scopeControl}

            <div className="pos-summary-head">
              {/* What the customers actually paid. The breakdown below is the
                  products' own revenue, which on VAT Exclusive is the smaller
                  figure — hence the VAT note, so the two are not read as a
                  contradiction. */}
              <span className="pos-summary-total">{peso(summary.grossTotal)}</span>
              <span className="pos-summary-sub">
                {summary.orderCount} order{summary.orderCount === 1 ? '' : 's'} &middot; {summary.itemCount} items sold
                {summary.vat > 0 && (
                  <> &middot; {peso(summary.vat)} VAT {summary.grossTotal - summary.deliveryFees > summary.total ? 'added' : 'included'}</>
                )}
                {summary.deliveryFees > 0 && (
                  <> &middot; {peso(summary.deliveryFees)} delivery fees</>
                )}
              </span>
            </div>

            {/* One series, so no legend — the heading names it. The period
                buttons sit in one row beside the title, above the plot they
                change. */}
            <div className="pos-chart-head">
              <h3 className="pos-chart-title">
                {byQuantity ? 'Units sold' : 'Takings'} {singleDay ? 'by hour' : 'over time'}
              </h3>

              {/* Three controls, one row, all above the plot they change:
                  what is counted, how it is split, and how finely it is
                  bucketed. */}
              <div className="pos-chart-controls">
                {paymentControl}

                <div className="pos-gran-toggle" role="group" aria-label="Measure">
                  {POS_TREND_MEASURES.map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      className={`pos-gran-btn${measure === key ? ' active' : ''}`}
                      onClick={() => setMeasure(key)}
                      aria-pressed={measure === key}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                <div className="pos-gran-toggle" role="group" aria-label="Split">
                  {POS_TREND_VIEWS.map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      className={`pos-gran-btn${view === key ? ' active' : ''}`}
                      onClick={() => setView(key)}
                      aria-pressed={view === key}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {/* Hidden for a one-day report, which is always hourly —
                    every choice here would plot that day as one point. */}
                {!singleDay && (
                <div className="pos-gran-toggle" role="group" aria-label="Trend period">
                  {POS_SALES_GRANULARITIES.map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      className={`pos-gran-btn${granularity === key ? ' active' : ''}`}
                      onClick={() => setGranularity(key)}
                      aria-pressed={granularity === key}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                )}
              </div>
            </div>

            <PosSalesTrendChart
              points={trend.points}
              series={series}
              tooltips={tooltips}
              formatValue={formatMeasure}
              // The peak's cost beside its takings, when costs are shown.
              peakNote={(i) => (showCost && !byQuantity && trend.points[i]?.cost !== null && trend.points[i]?.cost !== undefined
                ? `Cost ${peso(trend.points[i].cost)}`
                : '')}
              summary={
                `${granularityLabel} ${byQuantity ? 'units sold' : 'takings'}`
                + ` over ${trend.points.length} period${trend.points.length === 1 ? '' : 's'}`
                + `, ${view === 'overall' ? 'all categories combined' : `${series.length} categories`}`
              }
            />

            {/* Says so rather than quietly plotting a fraction of the period
                as though it were all of it. */}
            {trend.truncated && (
              <p className="pos-chart-note">
                Too many {granularityLabel.toLowerCase()} periods to plot &mdash; showing the first
                {' '}{trend.points.length}. Choose a wider period above, or a shorter date range.
              </p>
            )}

            {/* Scrollable on a phone: five columns cannot honestly
                fit a 360px screen, and squeezing them makes the figures
                unreadable rather than the table narrower. */}
            <div className="pos-table-scroll">
              <table className="pos-breakdown">
              <thead>
                <tr>
                  <th scope="col">Category</th>
                  <th scope="col">Product</th>
                  <th scope="col" className="num">Units</th>
                  {/* What one cost the club, averaged like the price beside
                      it, when costs are shown. */}
                  {showCost && <th scope="col" className="num">Cost</th>}
                  {/* Replaces Share. A percentage of the period's total says
                      more about how long the range was than about the product;
                      what it sold FOR is the fact a reader can act on. */}
                  <th scope="col" className="num">Price</th>
                  {showCost && <th scope="col" className="num">Total cost</th>}
                  <th scope="col" className="num">Amount</th>
                </tr>
              </thead>
              <tbody>
                {rankedGroups.map((group) => group.products.map((product, pi) => (
                  <tr key={`${group.name}-${product.name}`}>
                    {/* The category cell spans its products, so the table reads
                        as groups rather than as a repeated column. */}
                    {pi === 0 && (
                      <th scope="row" rowSpan={group.products.length} className="pos-breakdown-group">
                        {/* Name and bars side by side, so the bars use the
                            empty column the rowspan leaves rather than pushing
                            the row taller. Stacks again on a phone, where
                            there is no empty column to use. */}
                        <div className="pos-breakdown-group-inner">
                          <div className="pos-breakdown-group-name">
                            {group.name}
                            {/* The subtotal in whatever the chart is counting,
                                so the two never quote different units for the
                                same category. */}
                            <small>{byQuantity ? `${group.quantity} sold` : peso(group.revenue)}</small>
                          </div>
                          {/* Shows the SHAPE of the category — one product
                              carrying it, or revenue spread evenly — which the
                              amounts beside it cannot. Draws nothing for a
                              single-product category. */}
                          <PosCategoryBars
                            products={group.products}
                            categoryName={group.name}
                            measure={measure}
                          />
                        </div>
                      </th>
                    )}
                    <td>{product.name}</td>
                    <td className="num">{product.quantity}</td>
                    {showCost && <td className="num pos-cost-cell">{costText(product.unitCost)}</td>}
                    {/* An average where the price moved mid-period, which is
                        why it is derived from what was taken rather than read
                        off today's product record. */}
                    <td className="num">{peso(product.unitPrice)}</td>
                    {showCost && <td className="num pos-cost-cell">{costText(product.cost)}</td>}
                    <td className="num">{peso(product.revenue)}</td>
                  </tr>
                )))}
              </tbody>
              <tfoot>
                <tr>
                  {/* Named "before VAT" only when that is actually a
                       different number — on No VAT and VAT Inclusive it is the
                       same total, and the qualifier would just raise a
                       question the screen then has to answer. */}
                  <th scope="row" colSpan={2}>{summary.grossTotal - summary.deliveryFees > summary.total ? 'Total before VAT' : 'Total'}</th>
                  <td className="num">{summary.itemCount}</td>
                  {/* Deliberately blank: an average unit price (or cost)
                      across Gatorade and Chippy is a number with no meaning,
                      and printing one would invite it to be quoted. */}
                  {showCost && <td />}
                  <td />
                  {showCost && <td className="num pos-cost-cell">{costText(summary.cost)}</td>}
                  <td className="num">{peso(summary.total)}</td>
                </tr>
              </tfoot>
              </table>
            </div>
          </section>
        </>
      )}

      {!fetching && scopedOrders && scopedOrders.length > 0 && (
        <>
          {/* ---------- Records ---------- */}
          <section className="pos-section">
            <div className="pos-section-head pos-section-head-row">
              <div>
                <h2>Preview ({previewOrders.length} order{previewOrders.length === 1 ? '' : 's'})</h2>
                <p>Tap a row to see its receipt{canDelete ? ' — a sale can be deleted from there' : ''}.</p>
              </div>
              <PosSaleRecordFilters
                status={statusFilter}
                onStatusChange={setStatusFilter}
                orderId={orderIdSearch}
                onOrderIdChange={setOrderIdSearch}
                search={recordSearch}
                onSearchChange={setRecordSearch}
              />
            </div>

            <div className="pos-table-scroll">
              <table className="pos-records">
                <thead>
                  <tr>
                    <th scope="col">Order ID</th>
                    <th scope="col">Date</th>
                    <th scope="col">Items</th>
                    <th scope="col" className="num">Qty</th>
                    {showCost && <th scope="col" className="num">Cost</th>}
                    <th scope="col" className="num">Total</th>
                    <th scope="col">Cashier</th>
                    {showDeletion && <th scope="col">Reason for deleting</th>}
                  </tr>
                </thead>
                <tbody>
                  {pageRows.length === 0 && (
                    <tr className="pos-records-empty">
                      <td colSpan={6 + (showCost ? 1 : 0) + (showDeletion ? 1 : 0)}>
                        {statusFilter === POS_SALE_STATUS_DELETED && !orderIdSearch.trim() && !recordSearch.trim()
                          ? 'No cancelled or deleted sales in this range.'
                          : 'No sales match these filters.'}
                      </td>
                    </tr>
                  )}
                  {pageRows.map((order) => {
                    const deletion = order[POS_ORDER_DELETION_KEY];
                    return (
                    <tr key={order.id} onClick={() => setViewing(order)} tabIndex={0}
                      className={deletion ? 'pos-records-deleted' : undefined}
                      onKeyDown={(e) => { if (e.key === 'Enter') setViewing(order); }}>
                      <td className="pos-records-id">
                        {order.id}
                        {deletion && <span className="pos-records-tag">Deleted</span>}
                      </td>
                      <td>{new Date(order.createdAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}</td>
                      <td>{(order.items || []).map(i => `${posOrderItemLabel(i)} (${i.quantity})`).join(', ')}</td>
                      <td className="num">{order.itemCount}</td>
                      {showCost && <td className="num pos-cost-cell">{costText(posOrderCost(order, costLookup))}</td>}
                      <td className="num">{peso(order.total)}</td>
                      <td>{order.cashier}</td>
                      {showDeletion && (
                        <td className="pos-records-reason">
                          {deletion ? (
                            <>
                              {deletion.reason}
                              <small>
                                {deletion.deletedBy}
                                {deletion.deletedAt ? ` · ${posDateTimeText(deletion.deletedAt)}` : ''}
                              </small>
                            </>
                          ) : '—'}
                        </td>
                      )}
                    </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <th scope="row" colSpan={4}>
                      {statusFilter === POS_SALE_STATUS_DELETED ? 'TOTAL DELETED' : 'TOTAL'}
                      {statusFilter !== POS_SALE_STATUS_COMPLETED && statusFilter !== POS_SALE_STATUS_DELETED
                        && <small className="pos-records-total-note"> (excludes deleted)</small>}
                    </th>
                    {/* The listed sales' cost — on the unfiltered Completed
                        view, the same sum as the breakdown's total cost. */}
                    {showCost && <td className="num pos-cost-cell">{costText(previewTotals.cost)}</td>}
                    {/* GROSS, matching the order.total column above it. */}
                    <td className="num">{peso(previewTotals.grossTotal)}</td>
                    <td />
                    {showDeletion && <td />}
                  </tr>
                </tfoot>
              </table>
            </div>

            <PosPager page={page} totalItems={previewOrders.length} onPageChange={setPage} />
          </section>

          {/* Hidden without the permission, not disabled: a greyed-out
              Export invites someone to keep tapping it, and the figures are
              already on screen — the file is the only thing being withheld. */}
          {canExport && (
            <button type="button" className="pos-btn pos-btn-primary pos-export" onClick={handleExport}>
              <FileSpreadsheet size={16} /> Export as Excel (.xlsx)
            </button>
          )}
        </>
      )}

      {viewing && (
        <div className="pos-modal-overlay">
          <div className="pos-modal pos-modal-receipt" role="dialog" aria-modal="true" aria-label="Order receipt">
            <header className="pos-modal-head">
              <h3>Receipt</h3>
              <button type="button" className="pos-modal-close" onClick={() => setViewing(null)} aria-label="Close"><X size={18} /></button>
            </header>
            <div className="pos-modal-body">
              {/* Above the receipt, so a deleted sale can never be read as a
                  live one. */}
              <PosSaleDeletionNote deletion={viewing[POS_ORDER_DELETION_KEY]} />
              {/* No payment note — this order is history, and the instruction
                  only makes sense before money changes hands. */}
              <PosReceipt order={viewing} courtName={config?.courtName} reference={viewing.id} />
            </div>
            <footer className="pos-modal-foot">
              {/* Left of the spacer, away from Close, so it is never hit by
                  aiming at the button that means the opposite. */}
              {canDelete && !posOrderIsDeleted(viewing) && (
                <button type="button" className="pos-btn pos-btn-danger" onClick={() => setDeletingSale(true)}>
                  Delete sale
                </button>
              )}
              <span className="pos-foot-spacer" />
              <button type="button" className="pos-btn pos-btn-primary" onClick={() => setViewing(null)}>Close</button>
            </footer>
          </div>
        </div>
      )}

      {viewing && deletingSale && (
        <PosDeleteSaleModal
          order={viewing}
          onDeleted={handleSaleDeleted}
          onClose={() => setDeletingSale(false)}
        />
      )}

      <Modal
        isOpen={!!modal}
        title={modal?.title || ''}
        message={modal?.message || ''}
        type={modal?.type || 'info'}
        confirmText="OK"
        onClose={() => setModal(null)}
      />
    </div>
  );
}

export default PosSales;
